import React, { useState, useEffect } from 'react';
import { updateBoat } from '../../services/AdminBoatService';
import { validateBoat } from '../../validations/admin/AdminBoatValidation';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClose } from '@fortawesome/free-solid-svg-icons';
import BoatEditForm from '../../forms/boats/BoatEditForm';
import '../../styles/AdminModal.css';
import BoatGalleryImages from './BoatGalleryImages';

const BoatEditModal = ({ isOpen, onClose, onSuccess, boat, categories }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    status: '',
    main_image: null,
    gallery_images: [],
    length: '',
    capacity: '',
    staterooms: '',
    bathrooms: '',
    featured: false,
    category: null,
    price: '',
    discount_price: '',
    note: '',
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const isFormValid = formData.name.trim() !== '';
  const hasChanges = boat && (
    boat.name !== formData.name ||
    boat.description !== formData.description ||
    boat.status !== formData.status ||
    boat.main_image !== formData.main_image ||
    boat.length !== formData.length ||
    boat.capacity !== formData.capacity ||
    boat.staterooms !== formData.staterooms ||
    boat.bathrooms !== formData.bathrooms ||
    boat.featured !== formData.featured ||
    boat.category?.id !== formData.category?.id
  );
  
  useEffect(() => {
    if (boat) {
      setFormData({
        name: boat.name || '',
        description: boat.description || '',
        status: boat.status || '',
        main_image: boat.main_image || null,
        gallery_images: Array.isArray(boat.gallery_images) 
          ? boat.gallery_images.map(img => ({
              id: img.id,
              url: img.image,
            }))
          : [],
        length: boat.length || '',
        capacity: boat.capacity || '',
        staterooms: boat.staterooms || '',
        bathrooms: boat.bathrooms || '',
        featured: boat.featured || false,
        category: boat.category || null,
        price: boat.price || '',
        discount_price: boat.discount_price || '',
        note: boat.note || '',
      });
      setErrors({});
    }
  }, [boat]);

  const handleIntegerChange = (e) => {
    const { name, value } = e.target;
    const integerValue = value.replace(/[^0-9]/g, '');
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: integerValue,
    }));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'category_id') {
      const selectedCategory = categories.find(cat => cat.id === parseInt(value));
      setFormData(prevFormData => ({
        ...prevFormData,
        category: selectedCategory
      }));
    } else if (name === 'main_image') {
      const file = e.target.files[0];
      setFormData((prevFormData) => ({
        ...prevFormData,
        main_image: file,
      }));
    } else {
      setFormData((prevFormData) => ({
        ...prevFormData,
        [name]: type === 'checkbox' ? checked : value,
      }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setFormData((prevFormData) => ({
      ...prevFormData,
      main_image: file,
    }));
  };

  const handleSave = async () => {
    console.log('Saving boat with data:', formData);
    const validationErrors = validateBoat(formData);
    if (Object.keys(validationErrors).length > 0) {
      console.log("validationErrors", validationErrors);
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    setErrors({});
    setError('');

    try {
      console.log('DEBUG main_image:', formData.main_image, 'type:', typeof formData.main_image, 'instanceof File:', formData.main_image instanceof File);
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('status', formData.status);
      formDataToSend.append('length', formData.length);
      formDataToSend.append('capacity', formData.capacity);
      formDataToSend.append('staterooms', formData.staterooms);
      formDataToSend.append('bathrooms', formData.bathrooms);
      formDataToSend.append('featured', formData.featured);
      formDataToSend.append('price', formData.price || '');
      formDataToSend.append('discount_price', formData.discount_price || '');
      formDataToSend.append('category', formData.category?.id);
      formDataToSend.append('note', formData.note || '');

      if (formData.main_image && formData.main_image instanceof File) {
        formDataToSend.append('main_image', formData.main_image);
      }

      const newGalleryImages = formData.gallery_images.filter((img) => img instanceof File);
      const existingGalleryImages = formData.gallery_images.filter((img) => typeof img === 'object' && img.id);

      newGalleryImages.forEach((image) => {
        formDataToSend.append('gallery_images', image);
      });

      existingGalleryImages.forEach((img) => {
        formDataToSend.append('existing_image_ids', img.id);
      });

      for (let pair of formDataToSend.entries()) {
        console.log('DEBUG FormData entry:', pair[0], pair[1]);
      }

      await updateBoat(boat.id, formDataToSend);
      await onSuccess();
      onClose();
    } catch (error) {
      console.error('Error updating boat:', error);
      setError('Error updating boat. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const sizeError = Object.keys(errors).some((key) => errors[key].includes("must be less than 2MB"));
  const galleryErrorIndexes = Object.keys(errors)
    .filter((key) => key.startsWith('gallery_images_'))
    .map((key) => parseInt(key.split('_')[2], 10));

  return (
    <div className="admin__modal">
      <div className="admin__modal-content">
        <div className="admin__modal-intro">
          <h2>Edit Boat</h2>
          <button className="admin__modal-close" onClick={onClose}>
            <FontAwesomeIcon icon={faClose} />
          </button>
        </div>
        {error && <div className="error-message">{error}</div>}
        <div className="admin__modal-body">
          <BoatEditForm
            formData={formData}
            errors={errors}
            categories={categories}
            handleChange={handleChange}
            handleFileChange={handleFileChange}
            handleIntegerChange={handleIntegerChange}
            
          />
        </div>
        <div className="admin__modal-gallery">
          <BoatGalleryImages
            galleryImages={formData.gallery_images}
            onChange={(updatedImages) =>
              setFormData((prev) => ({ ...prev, gallery_images: updatedImages }))
            }
            errorIndexes={galleryErrorIndexes}
          />
          {sizeError && (
            <div className="error-message">
              Image size must not exceed 2MB.
            </div>
          )}
        </div>
        <div className="admin__modal-buttons">
          <button className="button secondary" onClick={onClose} disabled={loading}>
            {loading ? 'Saving...' : 'Cancel'}
          </button>
          <button
            className="button"
            onClick={handleSave}
            disabled={loading || !isFormValid || !hasChanges}
          >
            {loading ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BoatEditModal;